import { all, call, put, takeLatest, select } from 'redux-saga/effects';
import envConfig from '../../config';
import SegmentConstants from '../../common/constants/SegmentConstants';
import SUCCESS_MESSAGES from '../../common/constants/SuccessMessagesConstants';

import {
  createSegmentV2,
  fetchSegments,
  fetchSegmentById,
  filterBySegment,
  selectSegment,
  pinSegment,
  deleteSegment,
  updateSegment,
  fetchSelectedSegmentResults,
  fetchSegmentResultsByIds,
} from '../routines';
import { request } from '../../common/util/RestClient';

const { tabs, levels: segmentLevels } = SegmentConstants;

// eslint-disable-next-line no-unused-vars
const { ALL, PUBLIC, PRIVATE, PINNED } = tabs;
const {
  ACCOUNT_RISK,
  PIPELINE_RISK,
  ORGANISATION_RISK,
  ACCOUNT_OPPORTUNITY,
  PIPELINE_OPPORTUNITY,
  ORGANISATION_OPPORTUNITY,
} = segmentLevels;

/**
 * Caution: mutates object
 * @param {*} object
 * @param {*} objectKey
 * @param {*} arrayKey
 * @param {*} childElement
 */
const pushToObjectKeyObjectArray = (
  object,
  objectKey,
  arrayKey,
  childElement
) => {
  if (object[objectKey][arrayKey] === undefined) {
    object[objectKey][arrayKey] = [];
  }
  object[objectKey][arrayKey].push(childElement);
};

const mergePipelinesInGroups = (object) => {
  const groups = Object.keys(object);
  groups.forEach((groupName) => {
    const group = object[groupName];

    const accountRisks = group[ACCOUNT_RISK] || [];
    const orgRisk = group[ORGANISATION_RISK] || [];
    group[PIPELINE_RISK] = [...accountRisks, ...orgRisk];

    const accountOpportunity = group[ACCOUNT_OPPORTUNITY] || [];
    const orgOpportunity = group[ORGANISATION_OPPORTUNITY] || [];
    group[PIPELINE_OPPORTUNITY] = [...accountOpportunity, ...orgOpportunity];
  });
};

const groupSegmentData = (segments, pinned = []) => {
  const groupedSegmentData = {
    ALL: {},
    PUBLIC: {},
    PRIVATE: {},
    PINNED: {}, // TODO: pinned
  };

  segments.forEach((segment) => {
    const { segmentId, segmentLevel, isPrivate, isPinned } = segment;

    // ALL TAB - START
    pushToObjectKeyObjectArray(groupedSegmentData, ALL, segmentLevel, segment);

    // ALL TAB - END

    // PUBLIC TAB - START
    if (isPinned) {
      pushToObjectKeyObjectArray(
        groupedSegmentData,
        PINNED,
        segmentLevel,
        segment
      );
    }
    // PUBLIC TAB - END

    // PUBLIC TAB - START
    if (!isPrivate) {
      pushToObjectKeyObjectArray(
        groupedSegmentData,
        PUBLIC,
        segmentLevel,
        segment
      );
    }
    // PUBLIC TAB - END

    // PRIVATE TAB - START
    if (isPrivate) {
      pushToObjectKeyObjectArray(
        groupedSegmentData,
        PRIVATE,
        segmentLevel,
        segment
      );
    }
    // PRIVATE TAB - END

    // PINNED TAB - START
    if (pinned.includes(segmentId)) {
      pushToObjectKeyObjectArray(
        groupedSegmentData,
        PUBLIC,
        segmentLevel,
        segment
      );
    }
    // PINNED TAB - END
  });

  return groupedSegmentData;
};
export function* fetchSegmentsSaga() {
  try {
    const URL = `${envConfig.APIG_URL}segment`;
    const response = yield call(request, URL);
    const segments = response.data;

    const pinned = [];
    const grouped = groupSegmentData(segments, pinned);
    mergePipelinesInGroups(grouped);
    yield put(fetchSegments.success(grouped));
  } catch (error) {
    yield put(fetchSegments.failure({ error }));
  } finally {
    yield put(fetchSegments.fulfill());
  }
}

export function* fetchSegmentsResultsByIdsSaga({ payload }) {
  try {
    const URL = (id) => `${envConfig.APIG_URL}segment/${id}/result`;
    const callsById = {};
    payload.forEach((id) => {
      callsById[id] = call(request, URL(id));
    });
    const response = yield all(callsById);

    yield put(
      fetchSegmentResultsByIds.success(
        Object.entries(response).map(([id, res]) => ({ id, ...res.data }))
      )
    );
  } catch (error) {
    yield put(fetchSegmentResultsByIds.failure({ error }));
  } finally {
    yield put(fetchSegmentResultsByIds.fulfill());
  }
}

export function* fetchSegmentByIdSaga({ payload }) {
  try {
    const URL = `${envConfig.APIG_URL}segment/${payload}`;
    const response = yield call(request, URL);
    const segment = response.data[0];
    yield put(fetchSegmentById.success(segment));
  } catch (error) {
    yield put(fetchSegmentById.failure({ error }));
  } finally {
    yield put(fetchSegmentById.fulfill());
  }
}

export function* createSegmentSaga({ payload }) {
  const { segment, history, silent } = payload;
  try {
    const URL = `${envConfig.APIG_URL}segment`;
    const config = {
      method: 'POST',
      payload: segment,
    };
    const response = yield call(request, URL, config);
    if (!silent) {
      yield put(
        createSegmentV2.success({
          response,
          successMessage: SUCCESS_MESSAGES.SAVE_SEGMENT,
        })
      );
      yield put(fetchSegmentById.trigger(response.data.segmentId));
      history.push('/app/segments');
      yield put(fetchSegments.trigger());
    }
  } catch (error) {
    yield put(createSegmentV2.failure({ error }));
  } finally {
    yield put(createSegmentV2.fulfill());
  }
}

export function* updateSegmentSaga({ payload }) {
  const { segment, id } = payload;
  const selectedSegment = yield select(
    (state) => state.segments.selectedSegment
  );

  try {
    const URL = `${envConfig.APIG_URL}segment/${id}`;
    const config = {
      method: 'PATCH',
      payload: segment,
    };
    const response = yield call(request, URL, config);
    yield put(
      updateSegment.success({
        response,
        successMessage: SUCCESS_MESSAGES.SAVE_SEGMENT,
      })
    );

    yield put(fetchSegments.trigger());
    if (
      selectedSegment &&
      selectedSegment.segmentId &&
      selectedSegment.segmentId === id
    ) {
      // only if the selected segment is the one that was edited
      const updatedSelectedSegment = { ...selectedSegment, ...segment };
      yield put(selectSegment.trigger(updatedSelectedSegment));
    }
  } catch (error) {
    yield put(updateSegment.failure({ error }));
  } finally {
    yield put(updateSegment.fulfill());
  }
}

export function* filterBySegmentSaga({ payload }) {
  try {
    const URL = `${envConfig.APIG_URL}segment/filter`;
    const config = {
      method: 'POST',
      payload: { logic: payload.logic },
    };
    const response = yield call(request, URL, config);
    yield put(filterBySegment.success(response.data));
  } catch (error) {
    yield put(filterBySegment.failure({ error }));
  } finally {
    yield put(filterBySegment.fulfill());
  }
}
export function* deleteSegmentSaga({ payload }) {
  try {
    const URL = `${envConfig.APIG_URL}segment/${payload}`;
    const config = {
      method: 'DELETE',
      payload: { segment: payload.segment },
    };
    const response = yield call(request, URL, config);
    yield put(
      deleteSegment.success({
        successMessage: 'Segment deleted successfully',
        response,
      })
    );
    yield put(fetchSegments.trigger());
  } catch (error) {
    yield put(deleteSegment.failure({ error }));
  } finally {
    yield put(deleteSegment.fulfill());
  }
}
// action needs to be only pin/unpin if calling this
export function* pinSegmentSaga({ payload }) {
  const { segmentId, action } = payload;
  try {
    const URL = `${envConfig.APIG_URL}segment/${segmentId}/pin/${action}`;
    const config = {
      method: 'POST',
      payload: { segment: payload.segment },
    };
    const response = yield call(request, URL, config);
    yield put(pinSegment.success(response.data));
    yield put(fetchSegments.trigger());
  } catch (error) {
    yield put(pinSegment.failure({ error }));
  } finally {
    yield put(pinSegment.fulfill());
  }
}
export function* selectSegmentSaga({ payload }) {
  yield put(filterBySegment.trigger({ logic: payload.logic }));
}
export function* fetchSelectedSegmentResultsSaga() {
  const selectedSegment = yield select(
    (state) => state.segments.selectedSegment
  );
  if (selectedSegment) {
    yield put(filterBySegment.trigger({ logic: selectedSegment.logic }));
  }
}

export default function* root() {
  yield all([
    takeLatest(filterBySegment.TRIGGER, filterBySegmentSaga),
    takeLatest(createSegmentV2.TRIGGER, createSegmentSaga),
    takeLatest(updateSegment.TRIGGER, updateSegmentSaga),
    takeLatest(fetchSegments.TRIGGER, fetchSegmentsSaga),
    takeLatest(fetchSegmentResultsByIds.TRIGGER, fetchSegmentsResultsByIdsSaga),
    takeLatest(fetchSegmentById.TRIGGER, fetchSegmentByIdSaga),
    takeLatest(selectSegment.TRIGGER, selectSegmentSaga),
    takeLatest(pinSegment.TRIGGER, pinSegmentSaga),
    takeLatest(deleteSegment.TRIGGER, deleteSegmentSaga),
    takeLatest(
      fetchSelectedSegmentResults.TRIGGER,
      fetchSelectedSegmentResultsSaga
    ),
  ]);
}
